import { useEffect, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { useTheme } from 'components/core/theme-provider';
import { Globe, Images, Network, Settings2, X, FileStack, ChevronDown } from 'lucide-react';
import { ButtonIds } from '../types/chat';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { SourcesSelector } from './SourcesSelector';
import gdriveIcon from 'assets/images/gdrive.svg';
import onedriveIcon from 'assets/images/onedrive.svg';
import { useIsMobile } from 'hooks/use-mobile';

interface ToolsSelectorProps {
    isFeatureSelected?: Record<ButtonIds, boolean>;
    onToggleTool: (toolId: ButtonIds) => void;
    disabled?: boolean;
    onOpenSourcesSelector?: () => void;
    selectedSources?: string[];
    isSourcesToolActive?: boolean;
    onToggleSourcesTool?: () => void;
    onSourcesChange?: (sources: string[]) => void;
    sourcesPopoverOpen?: boolean;
    onSourcesPopoverOpenChange?: (open: boolean) => void;
}

interface SourceConfig {
    id: string;
    name: string;
    icon?: string;
    color: string;
}

const sourceConfigs: Record<string, SourceConfig> = {
    'document-manager': {
        id: 'document-manager',
        name: 'DOCUMENT_MANAGEMENT_TITLE',
        color: '#8B5CF6',
    },
    'google-drive': {
        id: 'google-drive',
        name: 'Google Drive',
        icon: gdriveIcon,
        color: '#4285F4',
    },
    'onedrive': {
        id: 'onedrive',
        name: 'OneDrive',
        icon: onedriveIcon,
        color: '#0078D4',
    },
    'microsoft-sharepoint': {
        id: 'microsoft-sharepoint',
        name: 'SharePoint',
        color: '#0078D4',
    },
    'dropbox': {
        id: 'dropbox',
        name: 'Dropbox',
        color: '#0061FF',
    },
    'gmail': {
        id: 'gmail',
        name: 'Gmail',
        color: '#EA4335',
    },
    'google-calendar': {
        id: 'google-calendar',
        name: 'Google Calendar',
        color: '#4285F4',
    },
    'google-contacts': {
        id: 'google-contacts',
        name: 'Google Contacts',
        color: '#4285F4',
    },
    'box': {
        id: 'box',
        name: 'Box',
        color: '#0061D5',
    },
};

interface ToolConfig {
    id: ButtonIds | 'sources';
    name: string;
    icon: JSX.Element;
}

const getSourceConfig = (sourceId: string): SourceConfig => {
    return sourceConfigs[sourceId] || {
        id: sourceId,
        name: sourceId,
        color: '#6B7280',
    };
};

const buildTools = (translate: (key: string) => string): ToolConfig[] => [
    {
        id: 'sources',
        name: translate('SOURCES'),
        icon: <Network className="h-4 w-4" />,
    },
    {
        id: ButtonIds.SEARCH,
        name: translate('SEARCH'),
        icon: <Globe className="h-4 w-4" />,
    },
    {
        id: ButtonIds.CREATE_IMAGE,
        name: translate('CREATE_IMAGE'),
        icon: <Images className="h-4 w-4" />,
    },
];

const XL_BREAKPOINT = 1280;

const useIsXlUp = (): boolean => {
    const [isXlUp, setIsXlUp] = useState<boolean>(false);

    useEffect(() => {
        const mql = window.matchMedia(`(min-width: ${XL_BREAKPOINT}px)`);
        const onChange = (event: MediaQueryListEvent) => {
            setIsXlUp(event.matches);
        };

        setIsXlUp(mql.matches);
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, []);

    return isXlUp;
};

interface ActiveToolsDisplayProps {
    isFeatureSelected?: Record<ButtonIds, boolean>;
    onToggleTool: (toolId: ButtonIds) => void;
    onOpenSourcesSelector?: () => void;
    selectedSources?: string[];
    isSourcesToolActive?: boolean;
    onToggleSourcesTool?: () => void;
    onSourcesChange?: (sources: string[]) => void;
    sourcesPopoverOpen?: boolean;
    onSourcesPopoverOpenChange?: (open: boolean) => void;
    className?: string;
}

export const ActiveToolsDisplay = ({
    isFeatureSelected,
    onToggleTool,
    onOpenSourcesSelector,
    selectedSources = [],
    isSourcesToolActive = false,
    onToggleSourcesTool,
    onSourcesChange,
    sourcesPopoverOpen = false,
    onSourcesPopoverOpenChange,
    className = '',
}: ActiveToolsDisplayProps) => {
    const { theme } = useTheme();
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const isXlUp = useIsXlUp();

    const tools: ToolConfig[] = buildTools(t);
    const activeTools = tools.filter((tool) => tool.id !== 'sources' && isFeatureSelected?.[tool.id as ButtonIds]);
    const hasActiveTools = activeTools.length > 0 || isSourcesToolActive;

    const handleToolClick = (toolId: ButtonIds | 'sources') => {
        if (toolId === 'sources') {
            onToggleSourcesTool?.();
        } else {
            onToggleTool(toolId);
        }
    };

    if (!hasActiveTools) {
        return null;
    }

    return (
        <div className={`flex items-center gap-2 md:gap-4 ${className}`}>
            {isSourcesToolActive && (
                <div style={{ position: 'relative' }}>
                    {selectedSources.length > 0 ? (
                        <div
                            onClick={() => onOpenSourcesSelector?.()}
                            className={`relative flex items-center gap-1.5 h-[34px] px-1 sm:px-2.5 rounded-full border shadow-sm hover:bg-muted group cursor-pointer ${theme === 'dark'
                                ? 'border-primary'
                                : 'border-primary'
                                }`}
                        >
                            <div className="flex items-center gap-1">
                                {selectedSources.slice(0, isMobile ? 1 : 2).map((sourceId) => {
                                    const sourceConfig = getSourceConfig(sourceId);
                                    return (
                                        <TooltipProvider key={sourceId}>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <div
                                                        className="relative"
                                                        onClick={() => onOpenSourcesSelector?.()}
                                                    >
                                                        {sourceConfig.icon ? (
                                                            <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center border border-gray-200">
                                                                <img
                                                                    src={sourceConfig.icon}
                                                                    alt={sourceConfig.name}
                                                                    className="w-4 h-4"
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div
                                                                className="w-5 h-5 rounded-full flex items-center justify-center border border-white"
                                                                style={{ backgroundColor: sourceConfig.color }}
                                                            >
                                                                {sourceId === 'document-manager' ? (
                                                                    <FileStack className="h-3 w-3 text-white" />
                                                                ) : (
                                                                    <Network className="h-2.5 w-2.5 text-white" />
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p className="text-xs">{t(sourceConfig.name)}</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    );
                                })}
                                {selectedSources.length > (isMobile ? 1 : 2) && (
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div
                                                    className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-medium border ${theme === 'dark'
                                                        ? 'bg-gray-600 border-gray-500 text-gray-200'
                                                        : 'bg-gray-200 border-gray-300 text-gray-700'
                                                        }`}
                                                    onClick={() => onOpenSourcesSelector?.()}
                                                >
                                                    +{selectedSources.length - (isMobile ? 1 : 2)}
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <div className="text-xs">
                                                    {selectedSources.slice(isMobile ? 1 : 2).map((sourceId) => {
                                                        const sourceConfig = getSourceConfig(sourceId);
                                                        return (
                                                            <div key={sourceId}>
                                                                {t(sourceConfig.name)}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )}
                            </div>
                            <ChevronDown className="h-3 w-3 text-primary" />
                            {selectedSources.length === 0 && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onOpenSourcesSelector?.();
                                    }}
                                    className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center transition-opacity opacity-0 group-hover:opacity-100 ${theme === 'dark'
                                        ? 'bg-gray-700 hover:bg-gray-600 border-primary'
                                        : 'bg-white hover:bg-gray-100 border-primary'
                                        } border shadow-sm`}
                                    aria-label="Manage sources"
                                >
                                    <X className={`h-2.5 w-2.5 ${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />
                                </button>
                            )}
                        </div>
                    ) : (
                        <div
                            onClick={() => onOpenSourcesSelector?.()}
                            className={`relative flex items-center gap-2 h-[34px] min-w-[34px] px-3 rounded-full border shadow-sm hover:bg-muted group cursor-pointer ${theme === 'dark'
                                ? 'border-primary'
                                : 'border-primary'
                                }`}
                        >
                            <Network className={`h-4 w-4 ${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />
                            <span className={`text-sm hidden xl:block font-medium ${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'
                                }`}>
                                {t('SOURCES')}
                            </span>
                            <ChevronDown className={`h-3 w-3 ${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onToggleSourcesTool?.();
                                }}
                                className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center transition-opacity opacity-0 group-hover:opacity-100 ${theme === 'dark'
                                    ? 'bg-gray-700 hover:bg-gray-600 border-primary'
                                    : 'bg-white hover:bg-gray-100 border-primary'
                                    } border shadow-sm`}
                                aria-label="Remove sources tool"
                            >
                                <X className={`h-3 w-3 ${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />
                            </button>
                        </div>
                    )}

                    {/* Mount SourcesSelector ONLY for non-desktop to avoid duplicate popovers and wrong anchoring */}
                    {!isXlUp && (
                        <div style={{ position: 'absolute', left: 0, top: 0, opacity: 0, pointerEvents: 'none' }}>
                            <SourcesSelector
                                selectedSources={selectedSources}
                                onSourcesChange={onSourcesChange || (() => { /* no-op */ })}
                                disabled={isFeatureSelected?.[ButtonIds.SEARCH] || isFeatureSelected?.[ButtonIds.CREATE_IMAGE]}
                                externalOpen={sourcesPopoverOpen}
                                onExternalOpenChange={onSourcesPopoverOpenChange}
                            />
                        </div>
                    )}
                </div>
            )}

            {activeTools.map((tool) => (
                <TooltipProvider key={tool.id}>

                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div
                                className={`relative flex items-center gap-2 h-[34px] min-w-[34px] justify-center p-2  rounded-full border-[0.25px] shadow-sm hover:bg-muted group cursor-pointer ${theme === 'dark'

                                    ? 'border-primary-200 bg-[#fff0d026]'
                                    : 'border-primary-600 bg-[#fff0d080]'
                                    }`}
                            >
                                <div className={`${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`}>
                                    {tool.icon}
                                </div>
                                {/* <span className="text-sm font-medium text-primary ">{tool.name}</span> */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToolClick(tool.id);
                                    }}
                                    className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center transition-opacity opacity-0 group-hover:opacity-100 ${theme === 'dark'
                                        ? 'bg-gray-700 hover:bg-gray-600 border-primary'
                                        : 'bg-white hover:bg-gray-100 border-primary'
                                        } border shadow-sm`}
                                    aria-label={`Remove ${tool.name}`}
                                >
                                    <X className={`h-2.5 w-2.5 ${theme === 'dark' ? 'text-primary' : 'text-primary'}`} />
                                </button>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p className="text-xs">{tool.name}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            ))}
        </div>
    );
};

export const ToolsSelector = ({
    isFeatureSelected,
    onToggleTool,
    disabled = false,
    onOpenSourcesSelector,
    selectedSources = [],
    isSourcesToolActive = false,
    onToggleSourcesTool,
    onSourcesChange,
    sourcesPopoverOpen = false,
    onSourcesPopoverOpenChange,
}: ToolsSelectorProps) => {
    const [open, setOpen] = useState(false);
    const { theme } = useTheme();
    const { t } = useTranslation();

    const tools: ToolConfig[] = buildTools(t);

    const isMobile = useIsMobile();
    const isXlUp = useIsXlUp();

    const handleToolClick = (toolId: ButtonIds | 'sources') => {
        if (toolId === 'sources') {
            // Toggle the sources tool (make it active/inactive)
            onToggleSourcesTool?.();
            setOpen(false);
        } else {
            onToggleTool(toolId);
            setOpen(false);
        }
    };
    console.log('isSourcesToolActive', isSourcesToolActive);
    // Get active tools (excluding sources, we'll handle them separately)
    const activeTools = tools.filter(tool => {
        if (tool.id === 'sources') return false; // Don't include generic sources
        return isFeatureSelected?.[tool.id as ButtonIds];
    });
    const hasActiveTools = activeTools.length > 0 || isSourcesToolActive;

    return (
        <div className="flex items-center gap-4">
            {/* Tools Button - desktop (popover) */}
            <div className="xl:flex hidden">
                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                        <button
                            disabled={disabled}
                            aria-pressed="false"
                            aria-label="Tools"
                            className={
                                `flex h-[34px] min-w-[34px] items-center justify-center p-2 rounded-full border-[0.5px] ${theme === 'dark' ? 'border-primary-200' : 'border-primary-600'} shadow-sm hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed ` +
                                (theme === 'dark' ? 'border-primary-200' : 'border-primary-600') +
                                (hasActiveTools ? ' border-primary !text-primary' : '')
                            }
                        >
                            <Settings2 className={`h-4 w-4 ${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />
                        </button>
                    </PopoverTrigger>
                    <PopoverContent
                        className={`w-40 p-0`}
                        align="start"
                        side="top"
                        sideOffset={10}
                    >
                        <div className="flex flex-col p-1">
                            {tools.map((tool) => {
                                const isActive = tool.id === 'sources'
                                    ? isSourcesToolActive
                                    : isFeatureSelected?.[tool.id as ButtonIds];

                                return (
                                    <button
                                        key={tool.id}
                                        onClick={() => handleToolClick(tool.id)}
                                        className={`flex items-center gap-3 px-2 py-1.5   transition-colors text-left hover:bg-accent hover:text-accent-foreground ${isActive ? 'bg-muted' : ''}`}
                                    >
                                        <div className={`flex items-center justify-center text-primary`}>
                                            {tool.icon}
                                        </div>
                                        <span className={`text-sm font-light ${isActive ? 'text-primary' : ''} `}>
                                            {tool.name}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </PopoverContent>
                </Popover>
            </div>

            {/* Tools list - mobile/tablet (no popover) */}
            <div className="xl:hidden flex flex-col w-full">
                {tools.map((tool) => {
                    const isActive = tool.id === 'sources'
                        ? isSourcesToolActive
                        : isFeatureSelected?.[tool.id as ButtonIds];

                    return (
                        <button
                            key={tool.id}
                            disabled={disabled}
                            onClick={() => handleToolClick(tool.id)}
                            className={`flex items-center gap-3 px-2 py-1.5 transition-colors text-left hover:bg-muted ${isActive ? 'bg-muted' : ''}`}
                        >
                            <div className="flex items-center justify-center text-primary">
                                {tool.icon}
                            </div>
                            <span className={`text-sm  font-light ${isActive ? 'text-primary' : ''}`}>
                                {tool.name}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Active Tools Display - desktop only (mount only on xl+ to avoid hidden popover anchors) */}
            {isXlUp && hasActiveTools && (
                <div className="hidden xl:flex items-center gap-4">
                    {/* Sources tool button with popover */}
                    {isSourcesToolActive && (
                        <div style={{ position: 'relative' }}>
                            {selectedSources.length > 0 ? (
                                /* Merged button showing source icons without text */
                                <div
                                    onClick={() => onOpenSourcesSelector?.()}
                                    className={`relative flex items-center gap-1.5 px-1.5 lg:px-2.5 h-[34px] rounded-full border shadow-sm hover:bg-muted group cursor-pointer ${theme === 'dark'
                                        ? 'border-primary'
                                        : 'border-primary'
                                        }`}
                                >
                                    <div className="flex items-center gap-1">
                                        {selectedSources.slice(0, isMobile ? 1 : 2).map((sourceId) => {
                                            const sourceConfig = getSourceConfig(sourceId);
                                            return (
                                                <TooltipProvider key={sourceId}>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <div
                                                                className="relative"
                                                                onClick={() => onOpenSourcesSelector?.()}
                                                            >
                                                                {sourceConfig.icon ? (
                                                                    <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center border border-gray-200">
                                                                        <img
                                                                            src={sourceConfig.icon}
                                                                            alt={sourceConfig.name}
                                                                            className="w-4 h-4"
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <div
                                                                        className="w-5 h-5 rounded-full flex items-center justify-center border border-white"
                                                                        style={{ backgroundColor: sourceConfig.color }}
                                                                    >
                                                                        {sourceId === 'document-manager' ? (
                                                                            <FileStack className="h-3 w-3 text-white" />
                                                                        ) : (
                                                                            <Network className="h-2.5 w-2.5 text-white" />
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p className="text-xs">{t(sourceConfig.name)}</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            );
                                        })}
                                        {selectedSources.length > (isMobile ? 1 : 2) && (
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <div
                                                            className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-medium border ${theme === 'dark'
                                                                ? 'bg-gray-600 border-gray-500 text-gray-200'
                                                                : 'bg-gray-200 border-gray-300 text-gray-700'
                                                                }`}
                                                            onClick={() => onOpenSourcesSelector?.()}
                                                        >
                                                            +{selectedSources.length - (isMobile ? 1 : 2)}
                                                        </div>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <div className="text-xs">
                                                            {selectedSources.slice(isMobile ? 1 : 2).map((sourceId) => {
                                                                const sourceConfig = getSourceConfig(sourceId);
                                                                return (
                                                                    <div key={sourceId}>
                                                                        {t(sourceConfig.name)}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        )}
                                    </div>
                                    <ChevronDown className="h-3 w-3 text-primary" />
                                    {selectedSources.length == 0 && <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onOpenSourcesSelector?.();
                                        }}
                                        className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center transition-opacity opacity-0 group-hover:opacity-100 ${theme === 'dark'
                                            ? 'bg-gray-700 hover:bg-gray-600 border-primary'
                                            : 'bg-white hover:bg-gray-100 border-primary'
                                            } border shadow-sm`}
                                        aria-label="Manage sources"
                                    >
                                        <X className={`h-2.5 w-2.5 ${theme === 'dark' ? 'text-primary' : 'text-primary'}`} />
                                    </button>}
                                </div>
                            ) : (
                                /* Sources button with text and dropdown when no sources selected */
                                <div
                                    onClick={() => onOpenSourcesSelector?.()}
                                    className={`relative flex items-center gap-2 h-[34px] min-w-[34px] px-3 rounded-full border shadow-sm hover:bg-muted group cursor-pointer ${theme === 'dark'
                                        ? 'border-primary'
                                        : 'border-primary'
                                        }`}
                                >
                                    <Network className={`h-4 w-4 ${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />
                                    <span className={`text-sm font-medium ${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'
                                        }`}>
                                        {t('SOURCES')}
                                    </span>
                                    <ChevronDown className={`h-3 w-3 ${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onToggleSourcesTool?.();
                                        }}
                                        className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center transition-opacity opacity-0 group-hover:opacity-100 ${theme === 'dark'
                                            ? 'bg-gray-700 hover:bg-gray-600 border-primary'
                                            : 'bg-white hover:bg-gray-100 border-primary'
                                            } border shadow-sm`}
                                        aria-label="Remove sources tool"
                                    >
                                        <X className={`h-3 w-3 ${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />
                                    </button>
                                </div>
                            )
                            }

                            {/* SourcesSelector positioned below the Sources button - hidden trigger */}
                            <div style={{ position: 'absolute', left: 0, top: 0, marginTop: '8px', zIndex: 50, opacity: 0, pointerEvents: 'none' }}>
                                <SourcesSelector
                                    selectedSources={selectedSources}
                                    onSourcesChange={onSourcesChange || (() => { /* no-op */ })}
                                    disabled={isFeatureSelected?.[ButtonIds.SEARCH] || isFeatureSelected?.[ButtonIds.CREATE_IMAGE]}
                                    externalOpen={sourcesPopoverOpen}
                                    onExternalOpenChange={onSourcesPopoverOpenChange}
                                />
                            </div>
                        </div>
                    )}

                    {/* Display other active tools (Deep research, Create image) */}
                    {activeTools.map((tool) => (
                        <TooltipProvider key={tool.id}>

                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div
                                        className={`relative flex items-center gap-2 h-[34px] min-w-[34px] justify-center p-2  rounded-full border-[0.25px] shadow-sm hover:bg-muted group cursor-pointer ${theme === 'dark'

                                            ? 'border-primary-200 bg-[#fff0d026]'
                                            : 'border-primary-600 bg-[#fff0d080]'
                                            }`}
                                    >
                                        <div className={`${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`}>
                                            {tool.icon}
                                        </div>
                                        <span className={`text-sm font-medium ${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`}>{tool.name}</span>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleToolClick(tool.id);
                                            }}
                                            className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center transition-opacity opacity-0 group-hover:opacity-100 ${theme === 'dark'
                                                ? 'bg-gray-700 hover:bg-gray-600 border-primary'
                                                : 'bg-white hover:bg-gray-100 border-primary'
                                                } border shadow-sm`}
                                            aria-label={`Remove ${tool.name}`}
                                        >
                                            <X className={`h-2.5 w-2.5 ${theme === 'dark' ? 'text-primary-200' : 'text-primary-600'}`} />
                                        </button>
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p className="text-xs">{tool.name}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    ))}
                </div>
            )}
        </div>
    );
};
